import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { Button } from './button.tsx'
import { Icon } from './icon.tsx'
import { type IconName } from '@/icon-name'

const cardImageVariants = cva(
	'relative inline-flex flex-col items-start justify-center overflow-hidden bg-cover bg-center bg-no-repeat transition-all duration-300 ease-in-out',
	{
		variants: {
			// Fixed dimensions per Figma specs
			size: {
				default: [
					'h-[325px] w-[384px]',
					'rounded-3xl', // 24px border radius
					'shadow-[0px_12px_20px_-2px_rgba(217,217,217,1.00)]', // Exact Figma shadow
					'p-6', // 24px padding
					'gap-6', // 24px item spacing
				],
			},
		},
		defaultVariants: {
			size: 'default',
		},
	},
)

const cardImageTitleVariants = cva(
	'font-parkinsans origin-left transform-gpu justify-center self-stretch font-medium text-white transition-all duration-300 ease-in-out',
	{
		variants: {
			state: {
				default: 'scale-100 text-2xl leading-8', // 24px - Parkinsans/24px-Medium (line-height: 32px)
				hover: 'scale-100 text-[32px] leading-[45px]', // 32px - Parkinsans/32px-Medium exact Figma specs
			},
		},
		defaultVariants: {
			state: 'default',
		},
	},
)

export interface CardImageProps
	extends Omit<React.ComponentProps<'div'>, 'children'>,
		VariantProps<typeof cardImageVariants> {
	/**
	 * If set to `true`, the component will be rendered as a child within the component.
	 * This child component must be a valid React component.
	 */
	asChild?: boolean
	/**
	 * Background image URL for the card
	 */
	backgroundImage?: string
	/**
	 * Secondary background image URL for hover effect
	 */
	backgroundImageHover?: string
	/**
	 * Card title text
	 */
	title?: string
	/**
	 * Whether to show the title (default: true)
	 */
	showTitle?: boolean
	/**
	 * Whether to show the primary button (default: true)
	 */
	showButton?: boolean
	/**
	 * Whether to show the icon button (default: true)
	 */
	showIconButton?: boolean
	/**
	 * Primary button text
	 */
	buttonText?: string
	/**
	 * Primary button icon name (trailing)
	 */
	buttonIcon?: IconName
	/**
	 * Primary button click handler
	 */
	onButtonClick?: () => void
	/**
	 * Icon button icon name
	 */
	iconButtonIcon?: IconName
	/**
	 * Icon button click handler
	 */
	onIconButtonClick?: () => void
	/**
	 * Primary button loading state
	 */
	isButtonLoading?: boolean
	/**
	 * Icon button disabled state
	 */
	isIconButtonDisabled?: boolean
	/**
	 * Custom content to render instead of default layout
	 */
	children?: React.ReactNode
	/**
	 * Additional props for the primary button
	 */
	buttonProps?: Partial<React.ComponentProps<typeof Button>>
	/**
	 * Additional props for the icon button
	 */
	iconButtonProps?: Partial<React.ComponentProps<typeof Button>>
}

const CardImage = React.forwardRef<HTMLDivElement, CardImageProps>(
	(
		{
			className,
			size = 'default',
			asChild = false,
			backgroundImage,
			backgroundImageHover,
			title,
			showTitle = true,
			showButton = true,
			showIconButton = true,
			buttonText,
			buttonIcon,
			onButtonClick,
			iconButtonIcon,
			onIconButtonClick,
			isButtonLoading = false,
			isIconButtonDisabled = false,
			children,
			buttonProps,
			iconButtonProps,
			style,
			...props
		},
		ref,
	) => {
		const [isHovered, setIsHovered] = React.useState(false)

		const Comp = asChild ? Slot : 'div'

		// Background image logic with hover support - Optimized
		const currentBackgroundImage = React.useMemo(() => {
			if (backgroundImageHover && isHovered) {
				return backgroundImageHover
			}
			return backgroundImage
		}, [backgroundImage, backgroundImageHover, isHovered])

		// Background image style for internal div
		const backgroundStyle = React.useMemo(
			() => ({
				backgroundImage: currentBackgroundImage
					? `url(/resources/images?src=${encodeURIComponent(currentBackgroundImage)}&w=384&h=325&format=webp)`
					: undefined,
				backgroundSize: 'cover',
				backgroundPosition: 'center',
				backgroundRepeat: 'no-repeat',
			}),
			[currentBackgroundImage],
		)

		// Combined style for main container (without background)
		const combinedStyle = React.useMemo(
			() => ({
				...style,
			}),
			[style],
		)

		// Handle hover states for title animation
		const handleMouseEnter = () => setIsHovered(true)
		const handleMouseLeave = () => setIsHovered(false)

		// Custom children override default layout
		if (children) {
			return (
				<Comp
					ref={ref}
					className={cn(cardImageVariants({ size }), className)}
					style={combinedStyle}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					{...props}
				>
					{children}
				</Comp>
			)
		}

		return (
			<Comp
				ref={ref}
				className={cn(cardImageVariants({ size }), className)}
				style={combinedStyle}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				{...props}
			>
				{/* Background Image Layer with Stretch Effect */}
				<div
					className="absolute inset-0"
					style={{
						...backgroundStyle,
						transform:
							!backgroundImageHover && backgroundImage && isHovered
								? 'scale(1.05)'
								: 'scale(1)',
						filter:
							!backgroundImageHover && backgroundImage && isHovered
								? 'brightness(1.08) contrast(1.03) saturate(1.08)'
								: 'brightness(1) contrast(1) saturate(1)',
						transition: backgroundImageHover
							? 'background-image 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), filter 0.3s ease-out'
							: 'transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), filter 0.3s ease-out',
					}}
				/>

				{/* Content Layer */}
				<div className="relative z-10 flex flex-1 flex-col items-end justify-between self-stretch">
					{/* Icon Button - Top Right Position with Figma exact styling */}
					{showIconButton && (
						<Button
							variant="secondary"
							size="m"
							appearance="ghost"
							iconOnly
							leftIcon={
								iconButtonIcon ? (
									<Icon name={iconButtonIcon} size="md" />
								) : undefined
							}
							onClick={onIconButtonClick}
							disabled={isIconButtonDisabled}
							className="inline-flex items-center justify-center gap-[12.5px] overflow-hidden rounded-[48px] bg-green-50 p-3"
							{...iconButtonProps}
						/>
					)}

					{/* Title with hover animation - Figma exact classes */}
					{showTitle && title && (
						<h3
							className={cn(
								cardImageTitleVariants({
									state: isHovered ? 'hover' : 'default',
								}),
							)}
						>
							{title}
						</h3>
					)}
				</div>

				{/* Primary Button - Bottom Position with Figma exact styling */}
				{showButton && (
					<Button
						variant="primary"
						size="l"
						appearance="solid"
						onClick={onButtonClick}
						rightIcon={
							buttonIcon ? <Icon name={buttonIcon} size="sm" /> : undefined
						}
						isLoading={isButtonLoading}
						className="inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-blue-500 px-6 py-4"
						{...buttonProps}
					>
						{buttonText && (
							<span className="font-parkinsans justify-start text-center text-base leading-tight font-medium text-neutral-50">
								{buttonText}
							</span>
						)}
					</Button>
				)}
			</Comp>
		)
	},
)

CardImage.displayName = 'CardImage'

export { CardImage, cardImageVariants, cardImageTitleVariants }
